import React, { useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react";
import classnames from "classnames";
import { Classes, Drawer, Position, TextArea } from "@blueprintjs/core";
import { PlanrCheckbox } from "modules/common/components/planr/checkbox/PlanrCheckbox";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import { PlanrTag } from "modules/common/components/planr/tag/PlanrTag";
import moment from "moment";
import {
    basisLabels,
    buildRow,
    periodLabel,
    periodMonths,
    projectContributions,
    reasonLabels,
    ReasonType,
    roleTagLabels,
} from "../models/calc";
import { UnitEconomicsStoreType } from "../models/unit-economics-store";
import { hours, percent, ratio, rub, rubSign, signedPercent } from "./format";
import { utilizationTone } from "./SummaryPanel";
import styles from "./UnitEconomicsPage.module.scss";

const monthNames = moment.months();
const reasonOrder: ReasonType[] = ["overtime", "premium", "indexation", "absence", "other"];

export const EmployeeDrawer = observer(({ store, employeeId, focusReason, onClose }: EmployeeDrawerProps) => {
    const data = store.data;
    const employee = employeeId ? store.employee(employeeId) : null;
    const period = store.periodCode;
    const row = useMemo(() => (employee && data ? buildRow(employee, period, data) : null), [employee, data, period]);

    // Escape закрывает панель, даже если фокус ушёл из неё (например, после сохранения)
    const isOpen = !!employeeId;
    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [isOpen, onClose]);

    const reasonRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (focusReason && row) {
            // панель выезжает с анимацией — прокручиваем после неё
            const timer = setTimeout(() => reasonRef.current?.scrollIntoView({ behavior: "smooth" }), 250);
            return () => clearTimeout(timer);
        }
    }, [focusReason, row?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!employee || !row || !data) {
        return null;
    }

    const months = employee.months.filter((m) => periodMonths(period).includes(m.month));
    const projects = row.production ? projectContributions(employee, period, data) : [];
    const tone = utilizationTone(row.utilization, row.benchmark);

    return (
        <Drawer
            isOpen={!!employeeId}
            onClose={onClose}
            position={Position.RIGHT}
            size={720}
            className={styles.drawer}
            title={
                <div className={styles.drawerTitle}>
                    <span className={styles.drawerName}>{row.name}</span>
                    <span className={styles.drawerSub}>
                        {row.position} · {row.department}
                    </span>
                </div>
            }
        >
            <div className={classnames(Classes.DRAWER_BODY, styles.drawerBody)}>
                <div className={styles.drawerMeta}>
                    <PlanrTag type={row.role === "production" ? "success" : row.role === "gip" ? "primary" : "neutral"}>
                        {roleTagLabels[row.role]}
                    </PlanrTag>
                    <span className={styles.drawerPeriod}>{periodLabel(period, data.year)}</span>
                </div>

                {/* цепочка показателей */}
                <div className={styles.chain}>
                    <ChainItem
                        label="Стоимость часа"
                        value={`${rub(row.factHour)} ₽`}
                        tip={`Затраты ÷ оплаченные часы (отработано + отпуск + больничный). За отработанный час — ${rub(
                            row.workedHour
                        )} ₽`}
                    >
                        план {rub(row.planHour)} ₽ ·{" "}
                        <span
                            className={classnames({
                                [styles.negative]: row.anomaly && (row.deviation ?? 0) > 0,
                                [styles.info]: row.anomaly && (row.deviation ?? 0) < 0,
                            })}
                        >
                            {signedPercent(row.deviation)}
                        </span>
                    </ChainItem>
                    <ChainItem label="Загрузка проектами" value={percent(row.utilization)} tone={tone}>
                        {hours(row.projectHours)} из {hours(row.availableHours)} · цель {percent(row.benchmark)}
                    </ChainItem>
                    {row.production ? (
                        <ChainItem label="Маржа" value={rubSign(row.margin)} tone={row.margin >= 0 ? "good" : "bad"}>
                            {rub(row.output)} − {rub(row.factCost)} − {rub(row.overhead)}
                        </ChainItem>
                    ) : (
                        <ChainItem label="Себестоимость труда" value={rubSign(row.factCost)}>
                            входит в накладные компании
                        </ChainItem>
                    )}
                </div>

                <Section title="По месяцам">
                    <table className={styles.miniTable}>
                        <thead>
                            <tr>
                                <th>Месяц</th>
                                <th title="Норма по производственному календарю">Норма</th>
                                <th>Отработано</th>
                                <th>Проекты</th>
                                <th title="Отпуск и больничный">Отпуск, б/л</th>
                                <th title="Фактические затраты компании с налогами; в подсказке — план по штатному">
                                    Затраты
                                </th>
                                {row.production && <th>Выработка</th>}
                                {row.production && <th>Маржа</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {months.map((m) => {
                                const margin = m.output - m.factCost - m.overhead;
                                const absence = m.vacationHours + m.illnessHours;
                                return (
                                    <tr
                                        key={m.month}
                                        className={classnames({ [styles.mutedRow]: !m.production && row.production })}
                                    >
                                        <td>{monthNames[m.month - 1]}</td>
                                        <td>{hours(m.normHours)}</td>
                                        <td>
                                            {hours(m.workedHours)}
                                            {m.overtimeHours > 0 && (
                                                <span className={styles.overtime} title="Переработка">
                                                    +{hours(m.overtimeHours)}
                                                </span>
                                            )}
                                        </td>
                                        <td>{hours(m.projectHours)}</td>
                                        <td className={classnames({ [styles.warnText]: absence > 0 })}>
                                            {absence > 0 ? hours(absence) : "—"}
                                        </td>
                                        <td title={`План по штатному: ${rubSign(m.planCost)}`}>{rub(m.factCost)}</td>
                                        {row.production && <td>{m.production ? rub(m.output) : "—"}</td>}
                                        {row.production && (
                                            <td
                                                className={classnames({
                                                    [styles.positive]: m.production && margin > 0,
                                                    [styles.negative]: m.production && margin < 0,
                                                })}
                                            >
                                                {m.production ? rub(margin) : "—"}
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </Section>

                {row.production && (
                    <Section
                        title="Откуда выработка"
                        hint={`Труд на проекте × выручка проекта на 1 ₽ его производственных затрат. «ср.» — действующий проект, взята средняя отдача закрытых (${ratio(
                            data.portfolioRatio
                        )}); «огр.» — закрытый проект с неправдоподобно высокой отдачей, ограничена ${ratio(data.ratioCap)}`}
                    >
                        {projects.length ? (
                            <table className={styles.miniTable}>
                                <thead>
                                    <tr>
                                        <th>Проект</th>
                                        <th>Часы</th>
                                        <th>Труд</th>
                                        <th title="Выручки на 1 ₽ производственных затрат проекта">Отдача</th>
                                        <th>Выработка</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {projects.map((p) => (
                                        <tr key={p.orderId}>
                                            <td className={styles.projectCell}>
                                                <span
                                                    className={styles.projectDot}
                                                    style={
                                                        p.order?.color ? { backgroundColor: p.order.color } : undefined
                                                    }
                                                />
                                                <span title={p.order?.name}>
                                                    {p.order
                                                        ? `${p.order.number ? `${p.order.number} · ` : ""}${p.order.name}`
                                                        : "Проект удалён"}
                                                </span>
                                            </td>
                                            <td>{hours(p.hours)}</td>
                                            <td>{rub(p.labor)}</td>
                                            <td
                                                title={
                                                    p.order
                                                        ? `${basisLabels[p.order.basis]}. Поступления ${rubSign(
                                                              p.order.revenue
                                                          )}, договор ${rubSign(p.order.price)}, затраты ${rubSign(
                                                              p.order.ownLabor + p.order.otherCosts
                                                          )}`
                                                        : undefined
                                                }
                                            >
                                                ×{ratio(p.order?.ratio ?? 0)}
                                                {p.order?.basis === "portfolio" && (
                                                    <span className={styles.basisMark}>ср.</span>
                                                )}
                                                {p.order?.basis === "capped" && (
                                                    <span className={styles.basisMark}>огр.</span>
                                                )}
                                            </td>
                                            <td className={styles.strong}>{rub(p.output)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className={styles.emptyNote}>Нет часов на проектах в этом периоде</div>
                        )}
                    </Section>
                )}

                <div ref={reasonRef}>
                    <ReasonForm store={store} employeeId={row.id} />
                </div>
            </div>
        </Drawer>
    );
});

const ReasonForm = observer(({ store, employeeId }: { store: UnitEconomicsStoreType; employeeId: string }) => {
    const data = store.data!;
    const employee = store.employee(employeeId)!;
    const row = buildRow(employee, store.periodCode, data)!;
    const saved = row.reason;

    const [reasons, setReasons] = useState<ReasonType[]>(saved?.reasons ?? []);
    const [comment, setComment] = useState(saved?.comment ?? "");
    const [saving, setSaving] = useState(false);

    // смена сотрудника или периода — подтягиваем сохранённое
    useEffect(() => {
        setReasons(saved?.reasons ?? []);
        setComment(saved?.comment ?? "");
    }, [employeeId, store.period, saved?.id, saved?.modified]); // eslint-disable-line react-hooks/exhaustive-deps

    const dirty =
        comment.trim() !== (saved?.comment ?? "") ||
        reasons.slice().sort().join() !== (saved?.reasons ?? []).slice().sort().join();

    const toggle = (reason: ReasonType) =>
        setReasons((current) =>
            current.includes(reason) ? current.filter((r) => r !== reason) : [...current, reason]
        );

    const save = async () => {
        setSaving(true);
        await store.saveReason(employeeId, reasons, comment);
        setSaving(false);
    };

    return (
        <Section
            title="Причина отклонения стоимости часа"
            hint={
                row.anomaly
                    ? `Отклонение ${signedPercent(row.deviation)} — больше порога ${data.deviationThreshold}%`
                    : `Отклонение ${signedPercent(row.deviation)} — в пределах порога ${data.deviationThreshold}%`
            }
        >
            <div className={styles.reasons}>
                {reasonOrder.map((reason) => (
                    <PlanrCheckbox key={reason} checked={reasons.includes(reason)} onChange={() => toggle(reason)}>
                        {reasonLabels[reason]}
                        {row.hints.includes(reason) && (
                            <span className={styles.hint} title="Подсказка по табелю и штатному">
                                вероятно
                            </span>
                        )}
                    </PlanrCheckbox>
                ))}
            </div>
            <div className={`${Classes.INPUT_GROUP} planr-default-input ${styles.reasonComment}`}>
                <TextArea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Пояснение: например, «премия за сдачу объекта», «индексация с 1 мая»"
                    fill
                    maxLength={1000}
                    rows={2}
                />
            </div>
            <div className={styles.reasonFooter}>
                <span className={styles.reasonAuthor}>
                    {saved ? `Указал ${saved.author?.name || "—"}, ${saved.modified}` : "Причина не указана"}
                </span>
                <PlanrButton type="greenish" size="small" onClick={save} disabled={!dirty || saving} loading={saving}>
                    Сохранить
                </PlanrButton>
            </div>
        </Section>
    );
});

const ChainItem = ({
    label,
    value,
    tone,
    tip,
    children,
}: {
    label: string;
    value: string;
    tone?: string;
    tip?: string;
    children: React.ReactNode;
}) => (
    <div className={styles.chainItem} title={tip}>
        <span className={styles.kpiLabel}>{label}</span>
        <span className={classnames(styles.chainValue, tone ? styles[tone] : "")}>{value}</span>
        <span className={styles.kpiNote}>{children}</span>
    </div>
);

const Section = ({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) => (
    <section className={styles.section}>
        <div className={styles.sectionHead}>
            <span className={styles.sectionTitle}>{title}</span>
            {hint && <span className={styles.sectionHint}>{hint}</span>}
        </div>
        {children}
    </section>
);

interface EmployeeDrawerProps {
    store: UnitEconomicsStoreType;
    employeeId: string | null;
    focusReason: boolean;
    onClose: () => void;
}
